import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { T, useGT } from 'gt-react';
import { Action, Subject } from '@erp/authz';
import { AR_TASKS_PAGE_SIZES } from '@erp/rpc';
import Page from '@/components/page/Page';
import { DataTable } from '@/components/common/data-table/data-table';
import { DataTablePagination } from '@/components/common/data-table/data-table-pagination';
import { ListDetailSplit } from '@/components/common/list-detail-split/ListDetailSplit';
import { useListDetailSplit } from '@/components/common/list-detail-split/useListDetailSplit';
import { useCan } from '@/hooks/useCan';
import {
  invalidateArTasksQueries,
  useArTasksList,
  useArTasksSummary,
} from '@/api/rpc/hooks/accounts-receivable/useArBillingTasks';
import { getArTasksColumns } from './arTasksColumns';
import {
  arTasksPageCount,
  arTasksTabLabel,
  recordOverlay,
  buildArTasksListInput,
  type ArTaskRecord,
} from './arTasksPageState';
import type { ArTasksSearch } from './arTasksSearch';
import { ArTasksListHeader } from './ArTasksListHeader';
import { ArTasksBulkActions } from './ArTasksBulkActions';
import { ArTaskDetail } from './ArTaskDetail';
import { ArTasksRailRow } from './ArTasksRailRow';
import { ArTasksOverlays, type ArTasksOverlayState } from './ArTasksOverlays';
import { useArTasksTable } from './useArTasksTable';
import { assigneeGroupHeaderFn } from './ArTasksAssigneeGroupHeader';
import { arTasksGroupHiddenColumns } from './arTasksAssigneeGroups';
import { useOpenTaskRow } from './useOpenTaskRow';

const DETAIL_PANE_ID = 'ar-tasks-detail-pane';

const NO_OVERLAYS: ArTasksOverlayState = {
  invoiceId: null,
  salesOrderId: null,
  reassignTaskIds: [],
  closeTasks: [],
  rulesOpen: false,
};

/**
 * AR Tasks: open billing tasks with metrics on top. Built from the same
 * pieces as the cash receipts page (segmented tabs, DataTableToolbar, bulk
 * bar). A row opens the task in a side sheet; every other dialog and
 * slide-in is an existing one.
 */
export function ARTasksPage() {
  const gt = useGT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const search: ArTasksSearch = useSearch({
    from: '/authenticated/accounts-receivable/tasks',
  });
  const [canManageTasks] = useCan(Action.Update, Subject.Task);

  function updateSearch(updates: Partial<ArTasksSearch>, resetPage = true) {
    void navigate({
      to: '/accounts-receivable/tasks',
      search: {
        ...search,
        ...updates,
        page: resetPage ? undefined : updates.page,
      },
      replace: true,
    });
  }

  const listInput = buildArTasksListInput(search);
  const { rows, total, assigneeGroups, isLoading } = useArTasksList(listInput);
  const summary = useArTasksSummary();

  const [overlays, setOverlays] = useState<ArTasksOverlayState>(NO_OVERLAYS);
  function openOverlay(patch: Partial<ArTasksOverlayState>) {
    setOverlays({ ...NO_OVERLAYS, ...patch });
  }

  function openRecord(row: ArTaskRecord) {
    openOverlay(recordOverlay(row));
  }
  const split = useListDetailSplit({
    urlSelectedId: search.taskId ?? null,
    onSelectionChange: (taskId) =>
      updateSearch({ taskId: taskId ?? undefined, page: search.page }, false),
    openColumns: '1fr 3fr',
    isLoading,
  });
  const openRow = useOpenTaskRow(rows, split.detailId);

  const columns = getArTasksColumns(gt, {
    onOpenReference: openRecord,
    onReassign: (row) => openOverlay({ reassignTaskIds: [row.taskId] }),
  });

  const { table, selectedRows, clearSelection, resetLayout } = useArTasksTable({
    columns,
    rows,
    total,
    listInput,
    hiddenColumns: arTasksGroupHiddenColumns(search.groupBy),
    onPageChange: (nextPage, pageSize) =>
      updateSearch({ page: nextPage, pageSize }, false),
    onSortChange: (sort) => updateSearch(sort),
  });

  return (
    <Page
      title={gt('Accounts Receivable Tasks', {
        context: 'AR tasks page title',
      })}
    >
      <div className="space-y-6">
        <ArTasksListHeader
          summary={summary.data}
          summaryLoading={summary.isLoading}
          search={search}
          tab={listInput.tab}
          table={table}
          columns={columns}
          onResetLayout={resetLayout}
          onOpenRules={
            canManageTasks ? () => openOverlay({ rulesOpen: true }) : undefined
          }
          onSearchChange={updateSearch}
        />

        {!split.isOpen && (
          <ArTasksBulkActions
            selectedCount={selectedRows.length}
            canManageTasks={canManageTasks}
            onClear={clearSelection}
            onAssign={() =>
              openOverlay({
                reassignTaskIds: selectedRows.map((row) => row.taskId),
              })
            }
            onClose={() => openOverlay({ closeTasks: selectedRows })}
            table={table}
            columns={columns}
            selectedRows={selectedRows}
          />
        )}

        <ListDetailSplit
          split={split}
          className="h-[calc(100vh-22rem)] min-h-[520px]"
          items={rows}
          getItemId={(row) => row.taskId}
          isLoading={isLoading}
          detailPaneId={DETAIL_PANE_ID}
          railTitle={
            <span>
              {arTasksTabLabel(listInput.tab, gt)} · {total}
            </span>
          }
          railEmptyMessage={
            <T context="AR tasks rail empty state">No tasks found.</T>
          }
          renderRailRow={({ item, selected, onSelect }) => (
            <ArTasksRailRow
              item={item}
              selected={selected}
              onSelect={onSelect}
            />
          )}
          table={
            <DataTable
              className="h-full"
              table={table}
              isLoading={isLoading}
              enableColumnReorder
              onRowClick={(row) => split.select(row.taskId)}
              groupHeaderFn={assigneeGroupHeaderFn(assigneeGroups, table)}
              emptyMessage={gt('No tasks found.', {
                context: 'AR tasks table empty state',
              })}
            />
          }
          renderDetail={(taskId) => (
            <ArTaskDetail
              taskId={taskId}
              row={openRow?.taskId === taskId ? openRow : undefined}
              canManageTasks={canManageTasks}
              onCloseTask={(row) => openOverlay({ closeTasks: [row] })}
              onOpenRecord={openRecord}
            />
          )}
          pagination={
            <DataTablePagination
              table={table}
              pageCount={arTasksPageCount(total, listInput.pageSize)}
              pageIndex={listInput.page - 1}
              pageSizeOptions={AR_TASKS_PAGE_SIZES}
              onPageChange={(pageIndex) =>
                updateSearch({ page: pageIndex + 1 }, false)
              }
            />
          }
        />
      </div>

      <ArTasksOverlays
        state={overlays}
        onDismiss={() => setOverlays(NO_OVERLAYS)}
        onDone={() => {
          setOverlays(NO_OVERLAYS);
          clearSelection();
          invalidateArTasksQueries(queryClient);
        }}
        taskTypeIds={
          summary.data?.taskTypeIds ?? { toInvoice: null, toSend: null }
        }
      />
    </Page>
  );
}
